<template>
  <EditableList
    :items="modelValue"
    add-label="Add container"
    remove-label="Remove container"
    @add="addContainer"
    @remove="removeContainer"
  >
    <template #row="{ item: container, index }">
      <div class="container-fields">
        <StField label="Name">
          <StTextInput
            :model-value="container.name"
            placeholder="Container name"
            @update:model-value="updateField(index, 'name', $event)"
          />
        </StField>

        <StField label="Container type">
          <StSelect
            :model-value="container.containerType || 'standard'"
            @update:model-value="updateField(index, 'containerType', String($event))"
          >
            <option value="standard">standard</option>
            <option value="introduction">introduction</option>
          </StSelect>
        </StField>

        <StField label="Reference">
          <StSelect
            :model-value="container.ref ?? 'none'"
            @update:model-value="updateRef(index, String($event))"
          >
            <option value="none">none</option>
            <option value="battleTactics">battleTactics</option>
          </StSelect>
        </StField>

        <template v-if="!container.ref">
          <div class="field--full">
            <RuleComponentForm
              :model-value="container.components"
              @update:model-value="updateField(index, 'components', $event)"
            />
          </div>
        </template>
      </div>
    </template>
  </EditableList>
</template>

<script setup lang="ts">
import type { RuleContainer, BundleRef } from '@scrolldex/shared';
import { StField, StTextInput, StSelect } from '../ui/index.js';
import EditableList from '../EditableList.vue';
import RuleComponentForm from './RuleComponentForm.vue';

const props = defineProps<{
  modelValue: RuleContainer[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: RuleContainer[]): void;
}>();

function newContainer(): RuleContainer {
  return { name: '', containerType: 'standard', components: [] };
}

function addContainer() {
  emit('update:modelValue', [...props.modelValue, newContainer()]);
}

function removeContainer(index: number) {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  );
}

function updateField(index: number, field: keyof RuleContainer, value: unknown) {
  const updated = props.modelValue.map((container, i) => {
    if (i !== index) return container;
    return { ...container, [field]: value };
  });
  emit('update:modelValue', updated);
}

function updateRef(index: number, refValue: string) {
  const updated = props.modelValue.map((container, i) => {
    if (i !== index) return container;
    if (refValue === 'none') {
      const { ref: _omit, ...rest } = container;
      return { ...rest, components: container.components };
    }
    return { ...container, ref: refValue as BundleRef };
  });
  emit('update:modelValue', updated);
}
</script>

<style scoped>
.container-fields {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--st-space-2);
  align-items: end;
  width: 100%;
}

.container-fields :deep(.field--full) {
  grid-column: 1 / -1;
}
</style>
